import { Flex, Text } from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCcuaClient } from '~/apis/ccuaApi'
import { reportingApi } from '~/apis/reportingApi'
import { getApiInst } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import { captureCcuaGatewayQueryFailure } from '~/queries/ccuaGatewaySentry'
import { useGetCustomStatuses } from '~/queries/customStatusQueries'
import { useGetPbxLineExtensions } from '~/queries/extensionQueries'
import { isUserAdmin } from '~/utils/auth'

import { FeatureFlag } from '@mangovoice/constants'
import { useFeatureFlag } from '@mangovoice/core'
import {
  QueueDashboardDetail,
  QueueDashboardFilters,
  useQueueSettings,
} from '@mangovoice/reporting-ui'

import { useDashboardContext } from './context/DashboardContext'

export const QueueDashboardPage = () => {
  const { t, i18n } = useTranslation()
  const { pbxId, callCenterEnabled } = useDashboardContext()
  const { pbx, userRoles } = useUserState()
  const isAdmin = isUserAdmin(userRoles)
  const pbxUuid = pbx?.uuid ?? null
  const { isEnabled: enableQueueMetrics } = useFeatureFlag(
    FeatureFlag.ReportingQueueMetrics,
  )

  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null)
  const { settings: queueSettings, updateSettings: updateQueueSettings } =
    useQueueSettings()

  const {
    showQueueUpdateErrorModal,
    showQueueUpdateGenericErrorModal,
    showStatusUpdatedToast,
  } = useQueueAlerts()

  const { data: queueCustomStatuses } = useGetCustomStatuses(pbxId)
  const { data: queueExtensionsData } = useGetPbxLineExtensions({
    pbxId,
    options: {
      enabled: !!pbxId,
      gcTime: 60 * 60 * 1000,
    },
  })
  const queueExtensions = queueExtensionsData?.results ?? []

  const queueAgentActions = useMemo(
    () => ({
      onQueueLogin: async (extensionId: number) => {
        const response = await getApiInst().putQueueLogin(extensionId)
        if (!response.ok) throw response.data ?? response
      },
      onQueueLogout: async (extensionId: number) => {
        const response = await getApiInst().putQueueLogout(extensionId)
        if (!response.ok) throw response.data ?? response
      },
      onUpdateAgentStatus: async (
        customStatusId: string | null,
        extUuid?: string,
        inQueue?: boolean,
      ) => {
        if (!extUuid) throw new Error('Missing extension UUID')
        if (!pbxUuid) throw new Error('Missing PBX UUID')
        const response = await getCcuaClient().patchUserAgent(
          pbxUuid,
          extUuid,
          {
            agent_status_id: customStatusId,
            logged_in: inQueue,
          },
        )
        if (!response.ok) {
          const failure = {
            status: response.status,
            ...(response.data ?? {}),
          }
          captureCcuaGatewayQueryFailure(failure, {
            enabled: true,
            pbxId: pbx?.id ?? pbxId,
            source: 'queue-dashboard-update',
            ignoreQueueMembershipNotFound: true,
          })
          throw failure
        }
      },
      onQueueUpdateError: (error: unknown) => {
        if ((error as { status?: number })?.status === 404) {
          showQueueUpdateErrorModal()
        } else {
          showQueueUpdateGenericErrorModal()
        }
      },
      onStatusUpdated: showStatusUpdatedToast,
    }),
    [
      pbx?.id,
      pbxId,
      pbxUuid,
      showQueueUpdateErrorModal,
      showQueueUpdateGenericErrorModal,
      showStatusUpdatedToast,
    ],
  )

  if (!callCenterEnabled) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <Text>{t('dashboards.noDataAvailable')}</Text>
      </Flex>
    )
  }

  return (
    <>
      <QueueDashboardFilters
        api={reportingApi}
        t={t}
        locale={i18n.language}
        selectedPbxId={pbxId}
        selectedQueueId={selectedQueueId}
        onQueueChange={setSelectedQueueId}
        settings={queueSettings}
        onSettingsChange={updateQueueSettings}
      />
      <Flex
        padding="1.25rem"
        flexDirection="column"
        alignItems="center"
        gap="0.25rem"
        flex="1 0 0"
        alignSelf="stretch"
        overflow="auto"
      >
        <QueueDashboardDetail
          api={reportingApi}
          t={t}
          locale={i18n.language}
          selectedPbxId={pbxId}
          selectedPbxUuid={pbxUuid}
          selectedQueueId={selectedQueueId}
          settings={queueSettings}
          onSettingsChange={updateQueueSettings}
          isAdmin={isAdmin}
          enableQueueMetrics={enableQueueMetrics}
          customStatuses={queueCustomStatuses ?? []}
          extensions={queueExtensions}
          {...queueAgentActions}
        />
      </Flex>
    </>
  )
}
